import React from 'react';
import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import {
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { useMFASetup } from './hooks/useMFASetup';

const MFASetup: React.FC = () => {
  const {
    mfaEnabled, loading, step, setupData,
    verifyCode, disableCode, error, success,
    handleSetup, handleEnable, handleDisable,
    handleCancelSetup, handleVerifyCodeChange, handleDisableCodeChange,
  } = useMFASetup();

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center p-4">
          <Spinner animation="border" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  const inputStyle: React.CSSProperties = {
    padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e2e8f0', fontSize: '1.3rem',
    textAlign: 'center', letterSpacing: '0.4em', fontWeight: 700,
    maxWidth: '220px', margin: '0 auto',
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <ShieldCheckIcon style={{ width: '24px', height: '24px', color: mfaEnabled ? '#059669' : '#94a3b8' }} />
          <h5 className="mb-0" style={{ fontWeight: 700, color: '#0f172a' }}>Two-Factor Authentication</h5>
          {mfaEnabled ? (
            <span className="badge" style={{ background: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: 600, padding: '4px 10px', borderRadius: '6px' }}>Enabled</span>
          ) : (
            <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: 600, padding: '4px 10px', borderRadius: '6px' }}>Disabled</span>
          )}
        </div>

        {error && <Alert variant="danger" className="py-2 px-3" style={{ fontSize: '0.88rem' }}>{error}</Alert>}
        {success && <Alert variant="success" className="py-2 px-3" style={{ fontSize: '0.88rem' }}>{success}</Alert>}

        {/* Status view */}
        {step === 'status' && !mfaEnabled && (
          <div>
            <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6 }}>
              Add an extra layer of security to your account by enabling two-factor authentication.
              You'll need an authenticator app like <strong>Google Authenticator</strong>, <strong>Authy</strong>, or <strong>1Password</strong>.
            </p>
            <div className="d-flex flex-column gap-2 mb-4" style={{ maxWidth: '360px' }}>
              {[
                { icon: DevicePhoneMobileIcon, text: 'Install an authenticator app on your phone' },
                { icon: LockClosedIcon, text: 'Scan a QR code to link your account' },
                { icon: CheckCircleIcon, text: 'Enter a 6-digit code each time you log in' },
              ].map((item, i) => (
                <div key={`mfa-step-${i}`} className="d-flex align-items-center gap-2">
                  <item.icon style={{ width: '18px', height: '18px', color: '#3b82f6', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.85rem', color: '#475569' }}>{item.text}</span>
                </div>
              ))}
            </div>
            <Button onClick={handleSetup} style={{
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', border: 'none',
              borderRadius: '10px', padding: '10px 24px', fontWeight: 600, fontSize: '0.9rem',
            }}>
              <ShieldCheckIcon style={{ width: '18px', height: '18px', marginRight: '6px' }} />
              Set Up MFA
            </Button>
          </div>
        )}

        {/* MFA is enabled — option to disable */}
        {step === 'status' && mfaEnabled && (
          <div>
            <div className="d-flex align-items-center gap-2 mb-3 p-3" style={{ background: '#f0fdf4', borderRadius: '10px' }}>
              <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#16a34a' }} />
              <span style={{ color: '#166534', fontWeight: 600, fontSize: '0.9rem' }}>
                MFA is active. Your account requires a code from your authenticator app to sign in.
              </span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '12px' }}>
              To disable MFA, enter your current authenticator code:
            </p>
            <Form onSubmit={handleDisable} className="d-flex align-items-end gap-3">
              <Form.Control type="text" placeholder="000000" inputMode="numeric" pattern="[0-9]*"
                value={disableCode} onChange={(e) => handleDisableCodeChange(e.target.value)}
                required autoComplete="one-time-code" maxLength={6}
                style={{ ...inputStyle, maxWidth: '160px', fontSize: '1rem' }} />
              <Button type="submit" variant="outline-danger" disabled={disableCode.length !== 6}
                style={{ borderRadius: '10px', padding: '10px 20px', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                <XCircleIcon style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                Disable MFA
              </Button>
            </Form>
          </div>
        )}

        {/* Setup/verify step */}
        {step === 'verify' && setupData && (
          <div>
            <div className="mb-4">
              <p style={{ color: '#374151', fontWeight: 600, fontSize: '0.95rem', marginBottom: '8px' }}>
                Step 1: Scan this QR code with your authenticator app
              </p>
              <div className="text-center mb-3 p-3" style={{ background: '#f8fafc', borderRadius: '12px' }}>
                <img src={setupData.qr_code} alt="MFA QR Code" style={{ width: '200px', height: '200px' }} />
              </div>
              <p style={{ color: '#64748b', fontSize: '0.82rem', textAlign: 'center' }}>
                Can't scan? Enter this code manually:{' '}
                <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.15em' }}>
                  {setupData.secret}
                </code>
              </p>
            </div>

            <div>
              <p style={{ color: '#374151', fontWeight: 600, fontSize: '0.95rem', marginBottom: '8px' }}>
                Step 2: Enter the 6-digit code from your app to verify
              </p>
              <Form onSubmit={handleEnable}>
                <div className="text-center mb-3">
                  <Form.Control type="text" placeholder="000000" inputMode="numeric" pattern="[0-9]*"
                    value={verifyCode} onChange={(e) => handleVerifyCodeChange(e.target.value)}
                    required autoComplete="one-time-code" maxLength={6}
                    style={inputStyle} />
                </div>
                <div className="d-flex gap-2 justify-content-center">
                  <Button variant="outline-secondary" onClick={handleCancelSetup}
                    style={{ borderRadius: '10px', padding: '10px 20px', fontWeight: 600, fontSize: '0.85rem' }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={verifyCode.length !== 6}
                    style={{
                      background: 'linear-gradient(135deg, #059669, #10b981)', border: 'none',
                      borderRadius: '10px', padding: '10px 24px', fontWeight: 600, fontSize: '0.85rem',
                    }}>
                    <CheckCircleIcon style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                    Enable MFA
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MFASetup;
